import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ActionRole, ButtonType } from '@newroom-connect/library/enums';
import { IActionButtonEvent, IListItemClickedEvent, IListOptionsSort, ListOptionsSortDirection, ListItemType, IListConfiguration, IListConfigurationColumn, IListItem } from '@newroom-connect/library/interfaces';
import { ExtractPropertyPipe, IsNonEmptyArrayPipe, ToStringPipe, ToArrayPipe } from '@newroom-connect/library/pipes';

import { ButtonComponent } from '../../buttons/button/button.component';
import { IconComponent } from '../../icon/icon.component';
import { BaseListComponent } from '../abstract/base-list.component';

import { RowListItemComponent } from './row-list-item/row-list-item.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    IconComponent,
    RowListItemComponent,
    ExtractPropertyPipe,
    ToStringPipe,
    ToArrayPipe,
    IsNonEmptyArrayPipe
  ],
  selector: 'nrc-row-list',
  templateUrl: './row-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RowListComponent extends BaseListComponent {
  @Input({ required: true }) public config!: IListConfiguration;

  @Output() public sortEvent = new EventEmitter<IListOptionsSort>();
  @Output() public rowClickedEvent = new EventEmitter<IListItemClickedEvent>();
  @Output() public itemActionButtonEvent = new EventEmitter<IActionButtonEvent>();
  @Output() public configButtonClickEvent = new EventEmitter<{ index: number, key: string }>();
  @Output() public rowCollapsedEvent = new EventEmitter<IListItem>();

  public listItemType = ListItemType;
  public actionRole = ActionRole;
  public buttonType = ButtonType;

  /**
   * Build a sort event by the given key and direction which can be passed to the API.
   *
   * @param sortEventRaw The raw sort event object.
   * @param sortEventRaw.key The key to sort the data by.
   * @param sortEventRaw.direction The direction to sort the data by.
   *
   * @returns An object for the sort event.
   */
  private static buildSortEvent(sortEventRaw: { key: string; direction: ListOptionsSortDirection; }): IListOptionsSort {
    const sortEvent: IListOptionsSort = {};

    const keyParts = sortEventRaw.key.split('.');

    let sortEventPart: IListOptionsSort = sortEvent;

    for (let i = 0; i < keyParts.length; i++) {
      sortEventPart[keyParts[i]] = i < keyParts.length -1 ? {} : sortEventRaw.direction;

      sortEventPart = i < keyParts.length -1 ? sortEventPart[keyParts[i]] as IListOptionsSort : sortEventPart;
    }

    return sortEvent;
  }

  /**
   * Order the list by the given column and direction.
   *
   * @param column The column to order the list by.
   * @param direction The direction to order the list by.
   */
  public orderByColumn(column: IListConfigurationColumn, direction: ListOptionsSortDirection): void {
    this.resetColumnsOrderByState();

    column.orderBy = column.orderBy === direction ? undefined : direction;

    this.sortEvent.emit(RowListComponent.buildSortEvent({ key: column.key, direction }));
  }

  /**
   * Reset the order by state of all columns in the given config.
   */
  private resetColumnsOrderByState(): void {
    this.config.columns.forEach(column => column.orderBy = undefined);
  }

  /**
   * Toggle the collapse state of a row and emit the row collapsed event.
   *
   * @param item The item to toggle collapse state for.
   * @param index The index of the item in the items array.
   */
  public toggleRowCollapse(item: IListItem, index: number): void {
    // Create a clone of the items array
    this.items.update(items => {
      if (items) {
        items[index].isCollapsed = !items[index].isCollapsed;
      }

      return items ? [...items] : null;
    });

    this.rowCollapsedEvent.emit(item);
  }

  /**
   * Check if an item has children based on the config settings.
   *
   * @param item The item to check.
   * @returns True if the item has children, false otherwise.
   */
  public hasChildren(item: IListItem): boolean {
    if (!this.config.collapsibleRows) {
      return false;
    }

    // First check if item has direct children array
    if (item.childItems && Array.isArray(item.childItems) && item.childItems.length > 0) {
      return true;
    }

    // Then check if there are children in the item's value object
    if (this.config.collapsibleConfig?.childrenKey && item.value && typeof item.value === 'object') {
      const childrenKey = this.config.collapsibleConfig.childrenKey;
      const children = (item.value as Record<string, unknown>)[childrenKey];

      return Array.isArray(children) && children.length > 0;
    }

    return false;
  }

  /**
   * Get the collapsed state of an item.
   *
   * @param item The item to check.
   * @returns True if the item is collapsed, false otherwise.
   */
  public isCollapsed(item: IListItem): boolean {
    if (!this.config.collapsibleRows) {
      return false;
    }

    // If the item has an isCollapsed property, use that
    if (typeof item.isCollapsed !== 'undefined') {
      return item.isCollapsed;
    }

    // Otherwise check if there's a collapsedKey in the config
    if (this.config.collapsibleConfig?.collapsedKey && item.value && typeof item.value === 'object') {
      const collapsedKey = this.config.collapsibleConfig.collapsedKey;

      return !!(item.value as Record<string, unknown>)[collapsedKey];
    }

    // Default to collapsed
    return true;
  }
}
